import {
  Lead,
  GuideSection,
  P,
  UL,
  OL,
  LI,
  Term,
  GuideLink,
  Callout,
} from './guide-ui';

/** Body for "Detection-as-code" guide. */
export function DetectionAsCodeGuideBody() {
  return (
    <>
      <Lead>
        Detection-as-code applies software-engineering discipline to security
        detections: instead of clicking rules together in a console, you write them
        as files, version them in Git, review them in pull requests, test them in
        CI/CD, and deploy them programmatically. The result is detections that are
        higher quality, auditable, and safe to change. This guide explains what
        detection-as-code is and how to adopt it.
      </Lead>

      <GuideSection id="what-is-dac" title="What is detection-as-code?">
        <P>
          <Term>Detection-as-code (DaC)</Term> treats detection rules as software
          artifacts. Rules live as text files in a version-controlled repository,
          changes go through peer-reviewed pull requests, automated tests verify they
          fire correctly, and deployment happens through a pipeline rather than by
          hand in a UI. It is the same workflow modern teams already use for
          application code, applied to the SIEM&apos;s detection content.
        </P>
      </GuideSection>

      <GuideSection id="why" title="Why teams move to detection-as-code">
        <UL>
          <LI><Term>Rule quality</Term>: peer review catches mistakes before a noisy or broken rule reaches production.</LI>
          <LI><Term>Change tracking</Term>: Git history shows who changed which detection, when, and why.</LI>
          <LI><Term>Fast rollback</Term>: a problematic detection can be reverted like any code change.</LI>
          <LI><Term>Replication</Term>: the same vetted detection set can be deployed across environments consistently.</LI>
          <LI><Term>Collaboration</Term>: detection engineering becomes a team practice with a clear review trail.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="how-it-works" title="How it works">
        <OL>
          <LI><Term>Repository</Term>: store detection rules as files in Git (GitHub, GitLab).</LI>
          <LI><Term>Rule format</Term>: author in a format that supports automation: Sigma (vendor-neutral) or a native query language such as LPQL, SPL or KQL.</LI>
          <LI><Term>CI/CD testing</Term>: on every change, a pipeline validates syntax and runs tests, often generating test events (e.g. with Atomic Red Team) to confirm the rule actually fires.</LI>
          <LI><Term>Review &amp; merge</Term>: changes are peer-reviewed in a pull request before they land.</LI>
          <LI><Term>Deploy</Term>: merged rules are pushed to the SIEM programmatically.</LI>
        </OL>
      </GuideSection>

      <GuideSection id="sigma" title="Sigma: the portable detection format">
        <P>
          <Term>Sigma</Term> is an open-source, vendor-agnostic signature format for
          SIEM detections. You write the detection logic once and convert it to the
          native query language of your platform (SPL, KQL, Elastic DSL, and others)
          with the Sigma toolchain. Because it is text-based and portable, Sigma is a
          natural fit for a version-controlled, detection-as-code workflow.
        </P>
        <Callout title="Detection engineering is a lifecycle">
          <p>
            Detection-as-code is the delivery mechanism for{' '}
            <Term>detection engineering</Term>, the broader practice of researching
            threats, writing detections, testing them against real attack
            techniques, tuning out false positives, and retiring stale rules.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse supports detection-as-code">
        <P>
          In LogPulse, detections are LPQL queries: any saved LPQL search can become
          a custom detection, MITRE-tagged, and <Term>Sigma rules can be imported
          and transpiled to LPQL</Term>, so existing detection content carries over.
          AI agents can <Term>propose</Term> new detections over the{' '}
          <GuideLink href="/guides/mcp-for-security">MCP server</GuideLink>, created
          disabled until a human approves, a review gate that mirrors the
          pull-request model. See{' '}
          <GuideLink href="/guides/what-is-siem">what is SIEM</GuideLink> for where
          detections sit, and{' '}
          <GuideLink href="/siem">Security Monitoring (SIEM)</GuideLink> for the full
          detection capability.
        </P>
      </GuideSection>
    </>
  );
}
